import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, fmtINR, imgUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { ArrowLeft, Upload, Clock, Activity, ShieldAlert, Star, Loader2, ImagePlus, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const Row = ({ label, value, accent }) => (
  <div className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
    <span className="text-sm text-slate-500">{label}</span>
    <span className={`text-sm font-medium tabular-nums ${accent || ""}`}>{value}</span>
  </div>
);

export default function ProcedureDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [params] = useSearchParams();
  const [p, setP] = useState(null);
  const [history, setHistory] = useState({ logs: [], versions: [] });
  const [active, setActive] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => api.get(`/procedures/${id}`).then((r) => { setP(r.data); setActive(r.data.cover_image); });
  useEffect(() => { load(); api.get(`/procedures/${id}/history`).then((r) => setHistory(r.data)); }, [id]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      const gallery = [...(p.gallery || []), ...urls];
      const cover = p.cover_image || urls[0];
      await api.put(`/procedures/${id}`, { ...p, gallery, cover_image: cover });
      toast.success(`${urls.length} image(s) uploaded`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  if (!p) return <div className="text-slate-400">Loading…</div>;
  const gallery = [p.cover_image, ...(p.gallery || [])].filter(Boolean);
  const defaultTab = params.get("tab") || "pricing";

  return (
    <div className="space-y-6" data-testid="procedure-detail">
      <button onClick={() => navigate("/procedures")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft className="h-4 w-4" /> Back to Procedures</button>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Left */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="rounded-xl overflow-hidden border-slate-200 dark:border-slate-800 shadow-sm">
            <img src={imgUrl(active || gallery[0])} alt={p.name} className="w-full aspect-[4/3] object-cover bg-slate-100" data-testid="detail-main-image" />
          </Card>
          <div className="grid grid-cols-4 gap-2">
            {gallery.slice(0, 8).map((g, i) => (
              <button key={i} onClick={() => setActive(g)} className={`rounded-lg overflow-hidden border-2 ${active === g ? "border-blue-500" : "border-transparent"}`}>
                <img src={imgUrl(g)} alt="" className="w-full aspect-square object-cover bg-slate-100" />
              </button>
            ))}
          </div>
          {isAdmin && (
            <div>
              <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={upload} data-testid="file-input" />
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-image-btn"
                className="w-full border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl py-8 flex flex-col items-center gap-2 text-slate-500 hover:border-blue-400 hover:text-blue-600 transition-colors duration-200">
                {uploading ? <Loader2 className="h-6 w-6 animate-spin" /> : <ImagePlus className="h-6 w-6" />}
                <span className="text-sm font-medium">{uploading ? "Uploading…" : "Upload / Drop images"}</span>
                <span className="text-xs text-slate-400">Multiple upload supported</span>
              </button>
            </div>
          )}
        </div>

        {/* Right */}
        <div className="lg:col-span-3 space-y-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="bg-blue-50 text-blue-700 hover:bg-blue-50">{p.category}</Badge>
              <Badge variant="outline" className={p.status === "Active" ? "border-emerald-200 text-emerald-700 bg-emerald-50" : "border-slate-200 text-slate-500"}>{p.status}</Badge>
              {p.featured && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100"><Star className="h-3 w-3 mr-1" /> Featured</Badge>}
            </div>
            <h2 className="font-heading text-3xl font-extrabold tracking-tight mt-3">{p.name}</h2>
            <p className="text-slate-500 mt-2">{p.short_description}</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["Recovery", p.recovery_time, Clock], ["Duration", p.duration, Activity], ["Risk", p.risk_level, ShieldAlert], ["Popularity", `${p.popularity}%`, Star]].map(([l, v, Ic]) => (
              <Card key={l} className="p-4 rounded-xl border-slate-200 dark:border-slate-800 shadow-sm">
                <Ic className="h-4 w-4 text-slate-400 mb-2" />
                <div className="text-xs text-slate-400 uppercase tracking-wide">{l}</div>
                <div className="font-medium text-sm mt-0.5">{v || "-"}</div>
              </Card>
            ))}
          </div>

          <Tabs defaultValue={defaultTab}>
            <TabsList className="flex flex-wrap h-auto">
              <TabsTrigger value="pricing">Pricing</TabsTrigger>
              <TabsTrigger value="commission">Commission</TabsTrigger>
              <TabsTrigger value="about">About</TabsTrigger>
              <TabsTrigger value="faqs">FAQs</TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
            </TabsList>

            <TabsContent value="pricing">
              <Card className="p-6 rounded-xl border-slate-200 dark:border-slate-800 shadow-sm">
                <Row label="Minimum Price" value={fmtINR(p.min_price)} />
                <Row label="Average Price" value={fmtINR(p.avg_price)} />
                <Row label="Maximum Price" value={fmtINR(p.max_price)} />
                <Row label="Offer Price" value={fmtINR(p.offer_price)} accent="text-teal-600" />
                <Row label="Doctor Charges" value={fmtINR(p.doctor_fees)} />
                <Row label="Hospital Charges" value={fmtINR(p.hospital_charges)} />
                <Row label="Implant Cost" value={fmtINR(p.implant_cost)} />
                <Row label="Consumables" value={fmtINR(p.consumables)} />
                <Row label="OT Charges" value={fmtINR(p.ot_charges)} />
                <Row label="Medicine Charges" value={fmtINR(p.medicine_charges)} />
                <Row label="Follow-up Charges" value={fmtINR(p.followup_charges)} />
                <Row label="Taxes" value={fmtINR(p.taxes)} />
                <Row label="Net Cost" value={fmtINR(p.net_cost)} accent="font-semibold" />
                <Row label="Suggested Selling Price" value={fmtINR(p.suggested_selling_price)} accent="text-blue-600 font-semibold" />
                <Row label="Gross Profit" value={fmtINR(p.gross_profit)} accent="text-emerald-600" />
                <Row label="Profit %" value={`${p.profit_pct}%`} accent={p.profit_pct < 20 ? "text-red-600 font-semibold" : "text-emerald-600 font-semibold"} />
                {p.profit_pct < 20 && <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">Margin alert: profit below 20% threshold.</div>}
              </Card>
            </TabsContent>

            <TabsContent value="commission">
              <Card className="p-6 rounded-xl border-slate-200 dark:border-slate-800 shadow-sm">
                <Row label="Commission %" value={`${p.commission_pct}%`} />
                <Row label="Commission Amount" value={fmtINR(p.commission_amount)} />
                <Row label="Sales Executive Commission" value={fmtINR(p.sales_commission)} />
                <Row label="Counsellor Incentive" value={fmtINR(p.counsellor_incentive)} />
                <Row label="Marketing Commission" value={fmtINR(p.marketing_commission)} />
                <Row label="Doctor Incentive" value={fmtINR(p.doctor_incentive)} />
                <Row label="Referral Commission" value={fmtINR(p.referral_commission)} />
                <Row label="Hospital Share" value={fmtINR(p.hospital_share)} />
                <Row label="Company Profit" value={fmtINR(p.company_profit)} accent="text-blue-600 font-semibold" />
                <Row label="Net Margin" value={`${p.net_margin}%`} accent="font-semibold" />
                {p.commission_notes && <p className="text-sm text-slate-500 mt-3">{p.commission_notes}</p>}
              </Card>
            </TabsContent>

            <TabsContent value="about" className="space-y-4">
              <Card className="p-6 rounded-xl border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
                <div><h4 className="font-heading font-semibold mb-1">Description</h4><p className="text-sm text-slate-500">{p.long_description || "-"}</p></div>
                <div><h4 className="font-heading font-semibold mb-1">Benefits</h4><p className="text-sm text-slate-500">{p.benefits || "-"}</p></div>
                <div><h4 className="font-heading font-semibold mb-1">Ideal Candidate</h4><p className="text-sm text-slate-500">{p.ideal_candidate || "-"}</p></div>
                <div className="grid sm:grid-cols-2 gap-4 pt-2">
                  <div><h4 className="font-heading font-semibold mb-2 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Included</h4>
                    <ul className="space-y-1 text-sm text-slate-500">{(p.included || []).map((x, i) => <li key={i}>• {x}</li>)}</ul></div>
                  <div><h4 className="font-heading font-semibold mb-2 flex items-center gap-2"><XCircle className="h-4 w-4 text-red-400" /> Not Included</h4>
                    <ul className="space-y-1 text-sm text-slate-500">{(p.not_included || []).map((x, i) => <li key={i}>• {x}</li>)}</ul></div>
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="faqs">
              <Card className="p-2 rounded-xl border-slate-200 dark:border-slate-800 shadow-sm">
                <Accordion type="single" collapsible>
                  {(p.faqs || []).map((f, i) => (
                    <AccordionItem key={i} value={`f${i}`}>
                      <AccordionTrigger className="px-4 text-sm">{f.q}</AccordionTrigger>
                      <AccordionContent className="px-4 text-slate-500">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Card>
            </TabsContent>

            <TabsContent value="history">
              <Card className="p-6 rounded-xl border-slate-200 dark:border-slate-800 shadow-sm">
                {history.logs.length === 0 && <p className="text-sm text-slate-400">No history yet.</p>}
                <div className="space-y-4">
                  {history.logs.map((l) => (
                    <div key={l.id} className="border-l-2 border-blue-200 pl-4">
                      <div className="text-sm font-medium capitalize">{l.action} · {l.email}</div>
                      <div className="text-xs text-slate-400">{new Date(l.timestamp).toLocaleString("en-IN")} · IP {l.ip}</div>
                      {l.changes && Object.keys(l.changes).length > 0 && (
                        <div className="mt-1 text-xs text-slate-500 space-y-0.5">
                          {Object.entries(l.changes).map(([k, v]) => <div key={k}>{k}: <span className="text-red-500 line-through">{String(v.old)}</span> → <span className="text-emerald-600">{String(v.new)}</span></div>)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
